import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns'; // Switch to regular date-fns
import { competitionConfigsService } from '../services/supabase';

const VersionHistoryDialog = ({ open, onClose, competitionId, onLoadVersion }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (open && competitionId) {
      loadVersions();
    }
  }, [open, competitionId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await competitionConfigsService.getVersions(competitionId);
      setVersions(data);
    } catch (err) {
      setError(err.message || 'Failed to load versions');
      console.error('Error loading versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (version) => {
    setDeleteConfirm(version);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await competitionConfigsService.deleteVersion(competitionId, deleteConfirm.revision);
      setSnackbar({ 
        open: true, 
        message: `Version ${deleteConfirm.revision} deleted successfully`, 
        severity: 'success' 
      });
      setDeleteConfirm(null);
      await loadVersions(); // Reload the list
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.message || 'Failed to delete version', 
        severity: 'error' 
      });
      console.error('Error deleting version:', err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isLatestVersion = (version) => {
    if (versions.length === 0) return false;
    return version.revision === versions[0].revision;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Version History
          <IconButton edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : versions.length === 0 ? (
          <Typography align="center">No versions found</Typography>
        ) : (
          <List>
            {versions.map((version) => (
              <ListItem
                key={version.revision}
                secondaryAction={
                  <Box display="flex" gap={1}>
                    <Button
                      startIcon={<RestoreIcon />}
                      onClick={() => onLoadVersion(version)}
                      size="small"
                    >
                      Restore
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(version)}
                      disabled={isLatestVersion(version) || versions.length === 1}
                      color="error"
                      size="small"
                    >
                      Delete
                    </Button>
                  </Box>
                }
              >
                <ListItemText
                  primary={`Version ${version.revision}${isLatestVersion(version) ? ' (Current)' : ''}`}
                  secondary={new Date(version.created_at + 'Z').toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true,
                    timeZoneName: 'short'
                  })}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete Version {deleteConfirm?.revision}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default VersionHistoryDialog;
